import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/Logo'
import { validateCaseId } from '../services/api'
import { CASE_ID_REGEX } from '../utils/constants'

export default function LandingPage() {
  const [rawInput, setRawInput] = useState('')
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()

  const caseId = rawInput.toUpperCase().trim()

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (!caseId) {
      setError('Please enter a Case ID.')
      return
    }

    if (!CASE_ID_REGEX.test(caseId)) {
      setError('Invalid Case ID format. Use letters, numbers, hyphens, and underscores (3-50 characters).')
      return
    }

    if (!pin || !/^\d{6}$/.test(pin)) {
      setError('Please enter a valid 6-digit PIN.')
      return
    }

    setLoading(true)
    try {
      await validateCaseId(caseId, pin)
      sessionStorage.setItem('viewerPin', pin)
      navigate(`/case/${caseId}`)
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Invalid Case ID or PIN.')
      } else if (err.response?.status === 400) {
        setError(err.response.data?.error || 'Invalid input. Please check and try again.')
      } else if (err.response?.status === 429) {
        setError(err.response.data?.error || 'Too many attempts. Please wait and try again.')
      } else {
        setError('Unable to validate. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <Logo size="lg" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">3D Model Viewer</h1>
          <p className="mt-1 text-sm text-gray-500">
            Enter your Case ID to view 3D surgical models
          </p>
        </div>

        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="caseId" className="mb-1 block text-sm font-medium text-gray-700">
                Case ID
              </label>
              <input
                id="caseId"
                type="text"
                value={rawInput}
                onChange={(e) => {
                  setRawInput(e.target.value)
                  setError('')
                }}
                placeholder="e.g. O3D-K7QM2P9X"
                className="input text-center text-lg font-mono tracking-wider uppercase"
                autoFocus
                autoComplete="off"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="viewerPin" className="mb-1 block text-sm font-medium text-gray-700">
                Viewer PIN
              </label>
              <input
                id="viewerPin"
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value.replace(/\D/g, '').slice(0, 6))
                  setError('')
                }}
                placeholder="6-digit PIN"
                className="input text-center text-2xl font-mono tracking-[0.5em]"
                autoComplete="off"
                disabled={loading}
              />
            </div>

            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            <button type="submit" className="btn-primary w-full" disabled={loading}>
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Verifying...
                </span>
              ) : (
                'View Models'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
